<template>
  <layout-content-vertical-nav :nav-menu-items="navigationItems">
    <slot />

    <!-- Slot: Navbar -->
    <template #navbar="{ isVerticalNavMenuActive, toggleVerticalNavMenuActive }">
      <div
        class="navbar-content-container"
        :class="{'expanded-search': shallShowFullSearch}"
      >
        <!-- Left Content: Search -->
        <div class="d-flex align-center">
          <v-icon
            v-if="$vuetify.breakpoint.mdAndDown"
            @click="toggleVerticalNavMenuActive"
          >
            {{ icons.mdiMenu }}
          </v-icon>
          <!--          <app-bar-search-->
          <!--            :shall-show-full-search.sync="shallShowFullSearch"-->
          <!--            :data="appBarSearchData"-->
          <!--            :filter="searchFilterFunc"-->
          <!--            :search-query.sync="appBarSearchQuery"-->
          <!--            @update:shallShowFullSearch="handleShallShowFullSearchUpdate(isVerticalNavMenuActive, toggleVerticalNavMenuActive)"-->
          <!--          />-->
        </div>

        <!-- Right Content: I18n, Light/Dark, Notification & User Dropdown -->
        <div class="d-flex align-center right-row">
          <pulse-button
            class="mr-2"
            class-name="tutorials"
            @click="setDialogState({dialogName: 'tutorialsVisible', value: true})"
          />
          <p class="ma-0 font-weight-semibold accent--text ml-3">
            {{ getCurrentUser ? getUserCompanyName : '' }}
          </p>
          <app-bar-i18n />
          <app-bar-theme-switcher />
          <app-bar-user-menu />
        </div>
      </div>
    </template>

    <!-- Slot: Footer -->
    <template #footer>
      <div class="d-flex justify-space-between">
        <span>COPYRIGHT &copy; {{ new Date().getFullYear() }} <a
          :href="siteUrl"
          target="_blank"
          class="text-decoration-none"
        >{{ appName }}</a><span class="d-none d-md-inline">, {{ $t("footerNav.All rights Reserved") }}</span></span>

        <div class="align-center d-none d-md-flex">
          <template v-for="(link, index) in links">
            <a
              :key="index"
              style="color: #878787"
              :href="link.href"
              target="_blank"
            >
              {{ link.name }}
            </a>
            <template v-if="links.length !== (index + 1)">
              <span
                :key="`pipe-${index}`"
                class="mx-1"
              >|</span>
            </template>
          </template>
        </div>
      </div>
    </template>
  </layout-content-vertical-nav>
</template>

<script>

// import AppBarSearch from '@core/layouts/components/app-bar/AppBarSearch.vue'
import { mdiMenu, mdiHeartOutline, mdiHelpCircleOutline } from '@mdi/js'
import { getVuetify } from '@core/utils'
import { ref, watch } from '@vue/composition-api'
import themeConfig from '@themeConfig'

import { mapGetters, mapMutations } from 'vuex'
import AppBarI18n from '@/layouts/components/app-bar/AppBarI18n.vue'
import AppBarThemeSwitcher from '@/layouts/components/app-bar/AppBarThemeSwitcher.vue'
import navMenuItems from '@/navigation/vertical'
import navMenuItemsMarketplace from '@/navigation/vertical/marketplace'
import AppBarUserMenu from '@/components/AppBarUserMenu.vue'
import LayoutContentVerticalNav from '@/layouts/variants/content/vertical-nav/components/LayoutContentVerticalNav.vue'

// Search Data
import appBarSearchData from '@/assets/app-bar-search-data'
import PulseButton from '@/components/common/Buttons/PulseButton.vue'

export default {
  components: {
    PulseButton,
    AppBarI18n,
    AppBarThemeSwitcher,
    LayoutContentVerticalNav,

    // App Bar Components
    // AppBarSearch,
    AppBarUserMenu,
  },

  computed: {
    ...mapGetters('user', ['getUserCompanyName', 'getCurrentUser']),

    links() {
      return this.$store.state.app.footer.links[this.$vuetify.lang.current]
    },

    siteUrl() {
      return this.$store.state.app.footer.siteUrl[this.$vuetify.lang.current]
    },

    navigationItems() {
      if (this.$route.meta.layout === 'marketplace') return navMenuItemsMarketplace

      return navMenuItems
    },
  },

  methods: {
    ...mapMutations('ui', ['setDialogState']),
  },

  setup() {
    const $vuetify = getVuetify()

    // Search
    const appBarSearchQuery = ref('')
    const shallShowFullSearch = ref(false)
    const maxItemsInGroup = 5
    const totalItemsInGroup = ref({
      pages: 0,
      files: 0,
      contacts: 0,
    })
    watch(appBarSearchQuery, () => {
      totalItemsInGroup.value = {
        pages: 0,
        files: 0,
        contacts: 0,
      }
    })

    // NOTE: Update search function according to your usage
    const searchFilterFunc = (item, queryText, itemText) => {
      if (item.header || item.divider) return true

      const itemGroup = (() => {
        if (item.to !== undefined) return 'pages'
        if (item.size !== undefined) return 'files'
        if (item.email !== undefined) return 'contacts'

        return null
      })()

      const isMatched = itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1

      if (isMatched) {
        if (itemGroup === 'pages') totalItemsInGroup.value.pages += 1
        else if (itemGroup === 'files') totalItemsInGroup.value.files += 1
        else if (itemGroup === 'contacts') totalItemsInGroup.value.contacts += 1
      }

      return appBarSearchQuery.value && isMatched && totalItemsInGroup.value[itemGroup] <= maxItemsInGroup
    }

    // ? Handles case where in <lg vertical nav menu is open and search is triggered using hotkey then searchbox is hidden behind vertical nav menu overlaty
    const handleShallShowFullSearchUpdate = (isVerticalNavMenuActive, toggleVerticalNavMenuActive) => {
      if ($vuetify.breakpoint.mdAndDown && isVerticalNavMenuActive) {
        toggleVerticalNavMenuActive()
      }
    }

    return {
      handleShallShowFullSearchUpdate,

      // Search
      appBarSearchQuery,
      shallShowFullSearch,
      appBarSearchData,
      searchFilterFunc,

      appName: themeConfig.app.name,

      icons: {
        mdiMenu,
        mdiHeartOutline,
        mdiHelpCircleOutline,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.navbar-content-container {
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  position: relative;
}

// ? Handle bg of autocomplete for blured appBar
.v-app-bar.bg-blur {
  .expanded-search {
    ::v-deep .app-bar-autocomplete-box div[role='combobox'] {
      background-color: transparent;
    }

    > .d-flex > button.v-icon {
      display: none;
    }

    // ===

    & > .right-row {
      visibility: hidden;
      opacity: 0;
    }

    ::v-deep .app-bar-search-toggler {
      visibility: hidden;
    }
  }
}
</style>
